/**
 * SHELL — results screen after the day's single run.
 *
 * Shows the run's metrics, the (server or pending) rank/percentile, the updated
 * streak, and the spoiler-free share action. A status banner communicates the
 * sync state (synced / pending / offline). The course is NOT replayed or drawn
 * here in a way that reveals the solution; we surface numbers only.
 */

import type { App } from '../../app';
import type { Screen } from '../router';
import { div, el } from '../dom';
import { streakBadge, metricGrid, banner, iconButton } from '../components';
import { formatTime, formatScore, formatRank, formatPercentile } from '../format';
import { shareResult } from '../../share/card';
import type { ShareData } from '../../share/card';
import { toast } from '../toast';
import { devToolsActive, devRetryDailyButton } from '../dev';

export function resultsScreen(app: App): Screen {
  const snap = app.store.get();
  const screen = div('dr-screen');

  const result = snap.result;
  if (!result) {
    // Defensive: no result to show (e.g. deep link). Send home.
    screen.append(banner('info', 'No run yet today. Give it a roll!'));
    screen.append(iconButton('dr-btn dr-btn--primary dr-btn--block', 'play', "Play today's roll", () => app.go('landing')));
    return { node: screen };
  }

  const { metrics, submission, synced } = result;
  const finished = metrics.finishTick >= 0;

  const card = div('dr-card');
  card.append(el('h2', { class: 'dr-h', text: finished ? 'Finished!' : 'Run complete' }));

  card.append(
    metricGrid([
      { label: finished ? 'Finish time' : 'Best time', value: finished ? formatTime(metrics.finishTimeMs) : '—' },
      { label: 'Checkpoints', value: `${metrics.checkpointsReached}` },
      { label: 'Score', value: formatScore(submission?.verifiedScore ?? metrics.score) },
      { label: 'Distance', value: `${Math.round(metrics.distance)}m` },
    ]),
  );

  // Rank / percentile (only when the server answered).
  const rank = submission?.rankGlobal;
  const pct = formatPercentile(submission?.percentile);
  if (rank !== undefined || pct) {
    card.append(
      div('dr-banner dr-banner--info',
        el('span', { text: pct ? `${pct} worldwide` : `Ranked ${formatRank(rank)} worldwide` }),
      ),
    );
  }

  screen.append(card);

  // Streak (server-authoritative when present, else local cache).
  const streakNow = submission?.streak ?? snap.streak.current;
  screen.append(streakBadge(streakNow, { best: snap.streak.best }));

  // Sync status.
  if (!synced) {
    screen.append(banner('warn', 'Saved locally. Your score will sync automatically when the leaderboard is ready.'));
  }

  // Primary actions.
  const shareData: ShareData = {
    dateKey: snap.dateKey,
    metrics,
    streak: streakNow,
    ...(submission?.rankGlobal !== undefined ? { rankGlobal: submission.rankGlobal } : {}),
    ...(submission?.percentile !== undefined ? { percentile: submission.percentile } : {}),
  };

  screen.append(
    iconButton('dr-btn dr-btn--primary dr-btn--lg dr-btn--block', 'share', 'Share result', () => {
      void share(shareData);
    }),
  );

  screen.append(
    div('dr-btn-row',
      iconButton('dr-btn dr-btn--ghost', 'globe', 'Leaderboard', () => app.go('leaderboard')),
      iconButton('dr-btn dr-btn--ghost', 'flame', 'Streak', () => app.go('streak')),
    ),
  );

  // Claim nudge for anonymous players (convert-to-account hook).
  if (snap.identity.kind !== 'claimed' && streakNow >= 1) {
    const claim = div('dr-card');
    claim.append(
      el('h2', { class: 'dr-h', text: 'Save your streak' }),
      el('p', { class: 'dr-muted', text: 'Claim your streak to keep it across devices and join friend leaderboards. Takes a few seconds — no password.' }),
      iconButton('dr-btn dr-btn--primary dr-btn--block', 'check', 'Claim my streak', () => app.go('claim')),
    );
    screen.append(claim);
  }

  // DEV ONLY — replay the scored daily run (gated behind the dev flag).
  if (devToolsActive()) {
    screen.append(div('dr-btn-row', devRetryDailyButton(app)));
  }

  return { node: screen };
}

async function share(data: ShareData): Promise<void> {
  const { method } = await shareResult(data);
  if (method === 'clipboard') toast('Result copied — paste it anywhere!');
  else if (method === 'download') toast('Result image saved.');
  else if (method === 'none') toast('Sharing unavailable on this device.');
}
